import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { siteData } from '../../data/content';
import SocialLinks from '../social/SocialLinks';
import * as FiIcons from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';

const { FiPhone, FiInstagram, FiFacebook } = FiIcons;

const LOGO_URL = "https://media-manager-c.questera.ai/greta-media/8c3dc8994f71a85ad2d71f226c89cc7edce114857865647a461869cb9a6c120ffe2f6c628544606d61dcf48135de6ac8/images/aW1hZ2UvcG5n/4b5d4e3c4fb29c1e85290ca636c01a9f.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const links = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Packages', path: '/packages' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed w-full z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
      {/* Top bar */}
      <div className="bg-blue-600 py-1.5 px-4 text-center text-xs text-white font-semibold tracking-wide">
        Serving Jacksonville, FL & Surrounding Areas —{' '}
        <a href="tel:9048312184" className="underline underline-offset-2 hover:text-blue-200 font-bold">
          Call (904) 831-2184
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center z-50 py-2">
            <img 
              src={LOGO_URL} 
              alt="A.R. Elite Detailing Logo" 
              className="h-14 md:h-16 w-auto object-contain drop-shadow-lg transition-transform hover:scale-105 duration-300"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-7">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-semibold transition-colors hover:text-blue-400 ${
                  isActive(link.path) ? 'text-blue-400' : 'text-slate-300'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Social icons in navbar */}
            <div className="flex items-center gap-2 border-l border-slate-800 pl-5">
              <a href={siteData.business.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-blue-400 transition-colors">
                <FiInstagram className="text-base" />
              </a>
              <a href={siteData.business.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-blue-400 transition-colors">
                <FiFacebook className="text-base" />
              </a>
              <a href={siteData.business.socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-blue-400 transition-colors">
                <SiTiktok className="text-sm" />
              </a>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="flex items-center gap-3 pl-2">
              <a href="tel:9048312184" className="hidden lg:flex items-center gap-2 text-slate-300 hover:text-blue-400 transition-colors font-bold text-sm uppercase tracking-wide">
                <FiPhone className="text-sm" /> (904) 831-2184
              </a>
              <a href="https://app.urable.com/virtual-shop/cFNwotdAvEx76Evup5X1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-sm font-bold transition-all text-sm uppercase tracking-wide shadow-[0_0_15px_rgba(37,99,235,0.3)]">
                Book Now
              </a>
            </div>
          </div>

          <button
            className="md:hidden text-slate-300"
            onClick={() => setIsOpen(!isOpen)}
          >
            <SafeIcon name={isOpen ? "X" : "Menu"} className="text-3xl" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 absolute w-full">
          <div className="px-4 pt-2 pb-6 space-y-2">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm"
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile CTA Buttons */}
            <div className="flex flex-col gap-3 mt-4 border-t border-slate-800 pt-4">
              <a href="https://app.urable.com/virtual-shop/cFNwotdAvEx76Evup5X1" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className="flex items-center justify-center gap-2 text-center bg-blue-600 text-white px-4 py-3 rounded-sm font-bold uppercase tracking-wide text-sm shadow-[0_0_15px_rgba(37,99,235,0.3)]">
                Book Now
              </a>
              <a href="tel:9048312184" onClick={() => setIsOpen(false)} className="flex items-center justify-center gap-2 text-center border border-slate-700 text-slate-300 px-4 py-3 rounded-sm font-bold uppercase tracking-wide text-sm">
                <FiPhone /> Call (904) 831-2184
              </a>
            </div>

            {/* Mobile social row */}
            <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-800 mt-2">
              <a href={siteData.business.socials.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 transition-colors">
                <FiInstagram className="text-xl" />
              </a>
              <a href={siteData.business.socials.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 transition-colors">
                <FiFacebook className="text-xl" />
              </a>
              <a href={siteData.business.socials.tiktok} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 transition-colors">
                <SiTiktok className="text-lg" />
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-950 border-t border-slate-900 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="A.R. Elite Detailing Logo" 
                className="h-20 md:h-24 w-auto object-contain drop-shadow-xl"
              />
            </Link>
            <p className="text-slate-400 mb-4 max-w-sm text-sm leading-relaxed">
              Premium auto detailing in Jacksonville, FL. Proudly veteran-owned and operated. Fresh Ride, Gloss Boss Detail, Elite Detail with ceramic protection, and more.
            </p>
            <p className="text-slate-500 text-xs mb-6 italic">
              Pricing may vary based on vehicle condition. Deposit may be required for new customers.
            </p>
            {/* Social block */}
            <div>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-3">
                Follow A.R. Elite Detailing
              </p>
              <SocialLinks variant="icons" />
              <p className="text-slate-600 text-xs mt-3">
                See more of our work on social media
              </p>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: 'Services', path: '/services' },
                { label: 'Packages', path: '/packages' },
                { label: 'Gallery', path: '/gallery' },
                { label: 'About', path: '/about' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Service Areas', path: '/areas' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.path} className="text-slate-400 hover:text-blue-400 transition-colors text-sm">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-400 text-sm">
                <SafeIcon name="MapPin" className="text-blue-500 mt-0.5 flex-shrink-0" />
                <span>Serving {siteData.business.location} & Surrounding Areas</span>
              </li>
              <li>
                <a href={`tel:${siteData.business.phoneTel}`} className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors text-sm">
                  <SafeIcon name="Phone" className="text-blue-500 flex-shrink-0" />
                  <span className="font-semibold">{siteData.business.phone}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <SafeIcon name="Mail" className="text-blue-500 flex-shrink-0" />
                <span>{siteData.business.email}</span>
              </li>
            </ul>
            
            {/* Footer CTA Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href="https://app.urable.com/virtual-shop/cFNwotdAvEx76Evup5X1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-sm hover:bg-blue-500 transition-all uppercase tracking-wide text-sm shadow-[0_0_15px_rgba(37,99,235,0.3)]">
                Book Now
              </a>
              <a href="tel:9048312184" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-700 text-slate-300 font-bold rounded-sm hover:border-blue-500 hover:text-blue-400 transition-all uppercase tracking-wide text-sm">
                <FiPhone className="text-sm" /> (904) 831-2184
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm flex flex-col sm:flex-row items-center gap-2 sm:gap-0">
            <span>&copy; {new Date().getFullYear()} {siteData.business.name}. All rights reserved.</span>
            <span className="hidden sm:inline mx-2">|</span>
            <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
          </p>
          <p className="text-slate-600 text-xs">Veteran Owned & Operated | Jacksonville, FL</p>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-[calc(5rem+2rem)]">
        {children}
      </main>
      <Footer />
    </div>
  );
}